import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormLabel,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { memberDeactivateAccountConfirmation } from '../api';
import { AuthContext } from '../context/AuthContext';
import SvgIcon from './SvgIcon';
import clockIcon from '../assets/icons/clock.svg';
import { useNavigate } from 'react-router-dom';
import { useAlertSnackbar } from '../hooks/useAlertSnackbar';

interface SmsVerificationDialogProps {
  open: boolean;
  handleClose: () => void;
  phoneNumber?: string;
  timeLeft: number;
  setTimeLeft: (timeLeft: number) => void;
}

const SmsVerificationDialog: React.FC<SmsVerificationDialogProps> = ({
  open,
  handleClose,
  phoneNumber,
  timeLeft,
  setTimeLeft,
}) => {
  const { t } = useTranslation();
  const { token } = useContext(AuthContext);
  const [error, setError] = useState<string | null>(null);
  const [otpCode, setCode] = useState<string>('');
  const nav = useNavigate();
  const showSnackbar = useAlertSnackbar(); // Use the Snackbar hook

  const handleCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setCode(value);
  };

  const handleConfirm = async (otpCode?: string) => {
    const result = await fetchDeActivateConfirmation(otpCode);
    if (result) {
      showSnackbar(t('accountDeactivateDialog.successMessage'), 'success'); // Show success Snackbar
      handleClose();
      localStorage.removeItem('id_token');
      nav('/login');
    }
  };

  const handleCloseDialog = () => {
    {
      handleClose();
      showSnackbar(
        t('accountDeactivateDialog.memberShipNotDeactivate'),
        'warning',
      );
    }
  };

  useEffect(() => {
    if (timeLeft === 0) return;

    const intervalId = setInterval(() => {
      setTimeLeft(timeLeft - 1);
    }, 1000);

    return () => clearInterval(intervalId);
  }, [timeLeft, setTimeLeft]);

  useEffect(() => {
    if (open) {
      setTimeLeft(179); // Reset the timer to 3 minutes when dialog opens
      setCode(''); // Clear the OTP code when dialog opens
    }
  }, [open, setTimeLeft]);

  const formatTime = () => {
    const minutes = Math.floor(timeLeft / 60)
      .toString()
      .padStart(2, '0');
    const seconds = (timeLeft % 60).toString().padStart(2, '0');
    return `${minutes}:${seconds}`;
  };

  const fetchDeActivateConfirmation = async (otpCode?: string) => {
    try {
      const res = await memberDeactivateAccountConfirmation(otpCode, token);
      if (res.status === 200) {
        setTimeLeft(179);
        return true;
      } else {
        const data = await res.json();
        throw data;
      }
    } catch (e: any) {
      const errorMessage = e.message || 'Error occurred';
      setError(errorMessage);
      showSnackbar(e.detail, 'error');
      return false;
    }
  };

  const maskPhoneNumber = (phoneNumber: string) => {
    if (phoneNumber.length <= 6) return phoneNumber;
    return (
      phoneNumber.slice(0, 7) +
      phoneNumber.slice(7, -1).replace(/./g, '*') +
      phoneNumber.slice(-1)
    );
  };

  const maskedPhoneNumber = maskPhoneNumber(
    phoneNumber ? phoneNumber : '**55********',
  );

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle>
        {t('accountDeactivateDialog.deactivateAccountTittle')}
      </DialogTitle>
      <DialogContent>
        <Box>
          <p>{t('accountDeactivateDialog.deactivateAccountMessage')}</p>
          <p>
            {t('accountDeactivateDialog.deactivateAccountMessageAttention')}
          </p>
          <Typography>
            {maskedPhoneNumber}{' '}
            {t('accountDeactivateDialog.smsValidationLabel')}
          </Typography>
          <Box sx={{ my: 2, display: 'flex', justifyContent: 'space-between' }}>
            <FormLabel htmlFor="otpCode">
              {t('validationForm.validationLabel')}
            </FormLabel>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <SvgIcon
                src={clockIcon}
                style={{
                  fill: '#2EB0A2',
                  width: 12,
                  height: 12,
                }}
              />
              <Typography sx={{ color: '#2CBCB1' }} ml={0.5} mt={0.5} mr={3}>
                {formatTime()}
              </Typography>
            </Box>
          </Box>
          <TextField
            type="text"
            name="otpCode"
            value={otpCode}
            onChange={handleCodeChange}
            color="secondary"
            size="small"
            disabled={timeLeft === 0}
            required
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleCloseDialog}
          color="secondary"
          variant="contained"
        >
          {t('accountDeactivateDialog.cancelDeactivateProgress')}
        </Button>
        <Button
          onClick={() => handleConfirm(otpCode)}
          color="error"
          variant="contained"
          disabled={timeLeft === 0 || otpCode.length < 6}
        >
          {t('accountDeactivateDialog.deactivateMemberShip')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SmsVerificationDialog;
